// @flow
import React, { useCallback, useMemo } from "react";
import { type TreeItemDTO } from "data/types";
import Tree from "components/Tree";
import styles from "./FolderTree.module.css";
import Row from "./Row";

type Props = {
    /** current node id to mark as active */
    activeTreeNodeId: number,
    /** tree items */
    tree: Array<TreeItemDTO>,
    /** callback when user clicks a tree item
     * @param {TreeItemDTO} treeItem tree item's object the user clicked
     * @param {SyntheticMouseEvent<*>} e HTML event object
     */
    onClick: (treeItem: TreeItemDTO, e: SyntheticMouseEvent<*>) => void,
    /** callback when user double-click a tree item
     * @param {TreeItemDTO} treeItem tree item's object the user clicked
     */
    onDoubleClick: (treeItem: TreeItemDTO) => void,
    /** callback when user expands a tree item
     * @param {TreeItemDTO} treeItem tree item's object the user clicked
     * @param {SyntheticMouseEvent<*>} e HTML event object
     */
    onExpand: (treeItem: TreeItemDTO, e: SyntheticMouseEvent<*>) => void,
    /** callback when user wants to load more results */
    onLoadMore: () => void,
    /** filter text currently applied */
    filter?: string,
    /** optional NoData Content Component */
    NoDataContent: Element<any>,
    /** total number of results available */
    totalCount: number,
    /** whether list is currently focused
     * @default {boolean} true
     */
    focused?: boolean,
};

const FolderTree = ({
    activeTreeNodeId,
    tree,
    onClick,
    onDoubleClick,
    onExpand,
    onLoadMore,
    filter,
    NoDataContent,
    totalCount,
    focused = true,
    viewName,
}: Props) => {
    const _hasMore = useMemo(
        () => tree && totalCount > tree.length,
        [totalCount, tree]
    );

    const _renderRow = useCallback(
        ({
            key, // Unique key within array of rows
            index, // Index of row within collection
            isScrolling, // The List is currently being scrolled
            isVisible, // This row is visible within the List (eg it is not an overscanned row)
            style, // Style object to be applied to row (to position it)
        }: {
            key: number,
            index: number,
            isScrolling: boolean,
            isVisible: boolean,
            style: StyleSheet,
        }) => (
            <Row
                key={key}
                index={index}
                isScrolling={isScrolling}
                isVisible={isVisible}
                style={style}
                hasMore={_hasMore}
                tree={tree}
                onLoadMore={onLoadMore}
                activeTreeNodeId={activeTreeNodeId}
                onDoubleClick={onDoubleClick}
                onClick={onClick}
                onExpand={onExpand}
                filter={filter}
                itemUri={tree[index]?.itemUri}
                viewName={viewName}
            />
        ),
        [
            tree,
            onClick,
            onDoubleClick,
            onExpand,
            onLoadMore,
            filter,
            activeTreeNodeId,
            _hasMore,
            viewName,
        ]
    );

    const _getActiveTreeNodeIndex = useCallback(
        () =>
            tree ? tree.findIndex((row) => row.id === activeTreeNodeId) : -1,
        [tree, activeTreeNodeId]
    );

    return (
        <Tree
            className={focused === false ? styles.unfocused : ""}
            filter={filter}
            activeTreeNodeId={activeTreeNodeId}
            scrollToIndex={_getActiveTreeNodeIndex()}
            totalItems={
                /* istanbul ignore next */ tree
                    ? tree.length + (_hasMore ? 1 : 0)
                    : 0
            }
            renderRow={_renderRow}
            locContext="folder"
            NoDataContent={NoDataContent}
        />
    );
};

export default FolderTree;
